import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button/Button';
import { CountBadge } from '@/design_system/CountBadge';
import Stack from '@/design_system/Stack/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList, TabPanel } from '@/design_system/Tabs/Tabs';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import { useShipments } from '@/models/shipment';
import { canCreateShipments, canVerifyReceptionOfShipments } from '@/models/user';
import { useCurrentSession } from '@/services/auth';
import { useResetSimpleBlocker } from '@/utils/navigation';

import { Filters } from './Filters/Filters';
import { FiltersStateContext, useFiltersState } from './Filters/FiltersState';
import { SHIPMENTS_PER_PAGE } from './Tabs/components/ShipmentsTable';
import { ShipmentsTab } from './Tabs/ShipmentsTab';

const Shipments = () => {
  const navigate = useNavigate();
  const { currentSession } = useCurrentSession();
  const { t } = useLingui();

  // Clear router blocker state after coming from shipments/new or shipment/edit
  useResetSimpleBlocker();

  const userCanCreateShipments = canCreateShipments(currentSession);
  const userCanVerifyReceptionOfShipments = canVerifyReceptionOfShipments(currentSession);
  const canAccessTodoTab = userCanCreateShipments || userCanVerifyReceptionOfShipments;

  const filters = useFiltersState({ canAccessTodoTab });

  const {
    data: { shipments = [], meta } = {},
    isLoading: isShipmentsLoading,
    isPlaceholderData: isShipmentsPlaceholderData,
    isError: isShipmentsError,
  } = useShipments(
    {
      limit: SHIPMENTS_PER_PAGE,
      offset: (filters.page.page - 1) * SHIPMENTS_PER_PAGE,
      search: filters.search.debouncedSearch.trim(),
      steps: filters.steps.steps,
      carriers: filters.carriers.carriers,
      originEntityIds: filters.origins.origins.map(({ id }) => id),
      destinationEntityIds: filters.destinations.destinations.map(({ id }) => id),
      tab: filters.tab.tab,
      sortField: filters.sort.sort.field,
      sortOrder: filters.sort.sort.order,
    },
    { keepPreviousData: true }
  );

  return (
    <>
      <div className="header">
        <Title size="200" as="h1" flex={1}>
          <Trans id="shipments.title">Shipments</Trans>
        </Title>
        {userCanCreateShipments && (
          <Button
            className="is-hidden-mobile"
            size="medium"
            onPress={() => navigate('/shipments/new')}
          >
            <IconAdd />
            <Trans id="shipments.new">New shipment</Trans>
          </Button>
        )}
        {userCanCreateShipments && (
          <Button
            className="is-hidden-from-tablet"
            size="large"
            iconOnly
            ariaLabel={t({ id: 'shipments.new', message: 'New shipment' })}
            onPress={() => navigate('/shipments/new')}
          >
            <IconAdd />
          </Button>
        )}
      </div>
      <div className="container container--list">
        <Stack gap="24px" style={{ height: '100%' }}>
          <Stack gap="1rem" style={{ flex: 1 }}>
            <FiltersStateContext value={filters}>
              <Filters />

              <Tabs
                gap="24px"
                selectedKey={filters.tab.tab}
                onSelectionChange={(tab) => filters.tab.setTab(tab as typeof filters.tab.tab)}
                style={{ flex: 1, paddingBottom: 24 }}
              >
                <TabList>
                  {canAccessTodoTab && (
                    <Tab id="to-do">
                      <Trans id="shipments.tab.to-do">To do</Trans>
                      {!!meta && (
                        <CountBadge
                          count={meta.countTodo}
                          countFormat="1k"
                          variant="neutral"
                          isActive={filters.tab.tab === 'to-do'}
                        />
                      )}
                    </Tab>
                  )}

                  <Tab id="waiting">
                    <Trans id="shipments.tab.waiting">Waiting</Trans>
                    {!!meta && (
                      <CountBadge
                        count={meta.countWaiting}
                        countFormat="1k"
                        variant="neutral"
                        isActive={filters.tab.tab === 'waiting'}
                      />
                    )}
                  </Tab>
                  <Tab id="all">
                    <Trans id="shipments.tab.all">All</Trans>
                    {!!meta && (
                      <CountBadge
                        count={meta.countAll}
                        countFormat="1k"
                        variant="neutral"
                        isActive={filters.tab.tab === 'all'}
                      />
                    )}
                  </Tab>
                </TabList>

                {canAccessTodoTab && (
                  <TabPanel id="to-do" style={{ flex: 1 }}>
                    <ShipmentsTab
                      {...{
                        tab: 'to-do',
                        shipments,
                        count: meta?.countTodo,
                        isLoading:
                          filters.tab.tab === 'to-do' &&
                          (isShipmentsLoading || isShipmentsPlaceholderData),
                        isError: filters.tab.tab === 'to-do' && isShipmentsError,
                        ...filters.page,
                      }}
                    />
                  </TabPanel>
                )}

                <TabPanel id="waiting" style={{ flex: 1 }}>
                  <ShipmentsTab
                    {...{
                      tab: 'waiting',
                      shipments,
                      count: meta?.countWaiting,
                      isLoading:
                        filters.tab.tab === 'waiting' &&
                        (isShipmentsLoading || isShipmentsPlaceholderData),
                      isError: filters.tab.tab === 'waiting' && isShipmentsError,
                      ...filters.page,
                    }}
                  />
                </TabPanel>

                <TabPanel id="all" style={{ flex: 1 }}>
                  <ShipmentsTab
                    {...{
                      tab: 'all',
                      shipments,
                      count: meta?.countAll,
                      isLoading:
                        filters.tab.tab === 'all' &&
                        (isShipmentsLoading || isShipmentsPlaceholderData),
                      isError: filters.tab.tab === 'all' && isShipmentsError,
                      ...filters.page,
                    }}
                  />
                </TabPanel>
              </Tabs>
            </FiltersStateContext>
          </Stack>
        </Stack>
      </div>
    </>
  );
};

export default Shipments;
